import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const STATUS = {
  disponivel: { label: "Disponível", color: "bg-[#00A859] text-white" },
  reservada: { label: "Reservada", color: "bg-[#FFCC00] text-foreground" },
  utilizada: { label: "Utilizada", color: "bg-[#71717A] text-white" },
  descartada: { label: "Descartada", color: "bg-foreground text-white" },
  vencida: { label: "Vencida", color: "bg-[#E60000] text-white" },
};

export default function Pulmao() {
  const [list, setList] = useState([]);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCidade, setFilterCidade] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const load = () => {
    const q = new URLSearchParams();
    if (filterStatus) q.append("status", filterStatus);
    if (filterCidade) q.append("cidade", filterCidade);
    api.get(`/pulmao/pecas?${q.toString()}`).then(r => setList(r.data));
  };
  useEffect(() => { load(); }, [filterStatus, filterCidade]);

  const submit = async () => {
    try {
      await api.post("/pulmao/pecas", form);
      toast.success("Peça cadastrada no pulmão");
      setOpen(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const mudarStatus = async (peca_id, novo) => {
    try {
      const fd = new FormData(); fd.append("status", novo);
      await api.post(`/pulmao/pecas/${peca_id}/status`, fd);
      toast.success("Status atualizado"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="label-cap">Pós-venda inteligente</div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Pulmão de Assistência</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Catálogo de peças reaproveitáveis (sobras de trocas, baixas, assistências finalizadas).
            O sistema sinaliza compatibilidades — a aprovação final é do administrativo.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="btn-sharp brand-bg text-white" data-testid="btn-nova-peca">+ Cadastrar peça</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Nova peça no pulmão</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <FF label="PIM origem" v={form.pim_origem} on={(v) => setForm(f => ({...f, pim_origem: v}))} testid="p-pim" />
                <FF label="Cliente origem" v={form.cliente_origem} on={(v) => setForm(f => ({...f, cliente_origem: v}))} testid="p-cli" />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <FF label="Produto *" v={form.produto} on={(v) => setForm(f => ({...f, produto: v}))} testid="p-prod" />
                <FF label="Modelo" v={form.modelo} on={(v) => setForm(f => ({...f, modelo: v}))} testid="p-modelo" />
                <FF label="Cor" v={form.cor} on={(v) => setForm(f => ({...f, cor: v}))} testid="p-cor" />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <FF label="Peça *" v={form.peca} on={(v) => setForm(f => ({...f, peca: v}))} testid="p-peca" />
                <FF label="Número da peça (manual)" v={form.numero_peca_manual} on={(v) => setForm(f => ({...f, numero_peca_manual: v}))} testid="p-num" />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="label-cap">Estado da peça</Label>
                  <Select value={form.estado_peca} onValueChange={(v) => setForm(f => ({...f, estado_peca: v}))}>
                    <SelectTrigger className="mt-2" data-testid="p-estado"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nova">Nova</SelectItem>
                      <SelectItem value="usada">Usada</SelectItem>
                      <SelectItem value="danificada">Danificada</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-cap">Condição operacional</Label>
                  <Select value={form.condicao_operacional} onValueChange={(v) => setForm(f => ({...f, condicao_operacional: v}))}>
                    <SelectTrigger className="mt-2" data-testid="peca-condicao"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="funcional">Funcional (pode ser sugerida)</SelectItem>
                      <SelectItem value="incompleta">Incompleta (não sugerida como solução completa)</SelectItem>
                      <SelectItem value="danificada">Danificada (nunca sugerida)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <FF label="Cidade *" v={form.cidade} on={(v) => setForm(f => ({...f, cidade: v}))} testid="p-cidade" />
                <FF label="Localização (depósito/técnico)" v={form.localizacao} on={(v) => setForm(f => ({...f, localizacao: v}))} testid="p-loc" />
              </div>
              <div>
                <Label className="label-cap">Observação</Label>
                <Textarea value={form.observacao} onChange={(e) => setForm(f => ({...f, observacao: e.target.value}))} className="mt-2" data-testid="p-obs" />
              </div>
              <Button onClick={submit} disabled={!form.produto || !form.peca || !form.cidade}
                className="w-full btn-sharp brand-bg text-white" data-testid="p-salvar">Cadastrar peça</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3 mb-6">
        <Select value={filterStatus || "todos"} onValueChange={(v) => setFilterStatus(v === "todos" ? "" : v)}>
          <SelectTrigger className="w-56" data-testid="filter-pulmao-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos</SelectItem>
            {Object.keys(STATUS).map(k => <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Filtrar por cidade" value={filterCidade}
          onChange={(e) => setFilterCidade(e.target.value)} className="w-64" data-testid="filter-pulmao-cidade" />
      </div>

      <div className="border border-border bg-white">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-[#F4F4F5]">
            <tr><Th>Código Origem</Th><Th>Produto</Th><Th>Peça</Th><Th>Estado</Th><Th>Cidade</Th><Th>Entrada</Th><Th>Validade</Th><Th>Status</Th><Th></Th></tr>
          </thead>
          <tbody>
            {list.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">Nenhuma peça cadastrada</td></tr>}
            {list.map((p) => (
              <tr key={p.id} className="border-b border-border last:border-0" data-testid={`peca-row-${p.id}`}>
                <Td className="font-mono text-xs">{p.pim_origem || "—"}</Td>
                <Td><strong>{p.produto}</strong>{p.modelo && <div className="text-xs text-muted-foreground">{p.modelo} · {p.cor}</div>}</Td>
                <Td>{p.peca}{p.numero_peca_manual && <div className="text-xs font-mono text-muted-foreground">#{p.numero_peca_manual}</div>}</Td>
                <Td>{p.estado_peca || "—"}<div className={`text-[10px] uppercase font-bold ${p.condicao_operacional === "danificada" ? "text-[#E60000]" : p.condicao_operacional === "incompleta" ? "text-[#C2410C]" : "text-emerald-700"}`} data-testid={`peca-cond-${p.id}`}>{p.condicao_operacional}</div></Td>
                <Td>{p.cidade}</Td>
                <Td className="font-mono text-xs">{p.data_entrada?.slice(0,10)}</Td>
                <Td className="font-mono text-xs">{p.validade_ate?.slice(0,10)}</Td>
                <Td>
                  <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${STATUS[p.status]?.color || "bg-secondary"}`}>
                    {STATUS[p.status]?.label || p.status}
                  </span>
                </Td>
                <Td>
                  <Select value="" onValueChange={(v) => mudarStatus(p.id, v)}>
                    <SelectTrigger className="h-8 w-32" data-testid={`peca-acao-${p.id}`}><SelectValue placeholder="Ação" /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(STATUS).map(k => <SelectItem key={k} value={k}>{STATUS[k].label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function emptyForm() {
  return { pim_origem: "", cliente_origem: "", produto: "", modelo: "", cor: "",
    peca: "", numero_peca_manual: "", estado_peca: "", condicao_operacional: "", cidade: "", localizacao: "", observacao: "" };
}
function FF({ label, v, on, testid }) {
  return <div><Label className="label-cap">{label}</Label><Input value={v||""} onChange={(e) => on(e.target.value)} className="mt-2" data-testid={testid} /></div>;
}
function Th({ children }) { return <th className="text-left p-3 label-cap font-medium">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
